import React, { lazy, Suspense, useEffect } from 'react';
import './App.css';
import { ColorModeContext, useMode } from './theme.jsx';
import { BrowserRouter, Route, Navigate } from 'react-router-dom';
import DataBuddiesStore from './redux/store';
import NavigationScroll from './components/layout/NavigationScroll';
import RoutesWithNotFound from './utilities/RoutesWithNotFound.utility';
import { PrivateRoutes, PublicRoutes } from './models/routes';
import Spinner from './components/Spinner';
import themes from './themes';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { CssBaseline, ThemeProvider } from '@mui/material';
import AuthGuard from './guards/auth.guard';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import enUS from 'date-fns/locale/de';
import { updateDomain } from './redux/states/domain'; // Removed .jsx if it's a .js file
import { fetchData } from './components/layout/App/Endpoints/Endpoints.jsx';

const Login = lazy(() => import('./pages/Login/Login'));
const ForgotPassword = lazy(
  () => import('./app/components/login/ForgotPassword.jsx'),
);
const ResetPassword = lazy(
  () => import('./app/components/login/ResetPassword.jsx'),
);
const Private = lazy(() => import('./pages/Private/Private'));

// Create an inner component to use hooks
function AppContent() {
  const [theme, colorMode] = useMode();
  const dispatch = useDispatch();
  const customization = useSelector((state) => state.customization);
  useEffect(() => {
    const fetchDataAsync = async () => {
      try {
        const currentDomain = window.location.origin;
        const response = await fetchData('view_aws_domain', {
          domain: currentDomain,
        });
        if (response?.aws_domain) {
          const matchingData = response.aws_domain.find(
            (item) => item.domain === currentDomain,
          );
          const parsedImageJson = JSON.parse(matchingData.image_json);
          dispatch(
            updateDomain({
              image_url: matchingData.image_url,
              image_json: parsedImageJson,
            }),
          );
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchDataAsync();
  }, []);

  return (
    <Suspense fallback={<Spinner />}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ColorModeContext.Provider value={colorMode}>
          <BrowserRouter>
            <ThemeProvider theme={themes(customization)}>
              <CssBaseline />
              <NavigationScroll>
                <RoutesWithNotFound>
                  <Route
                    path="/"
                    element={<Navigate to={PrivateRoutes.PRIVATE} />}
                  />
                  <Route path={PublicRoutes.LOGIN} element={<Login />} />
                  <Route
                    path={`/${PublicRoutes.FORGOTPASSWORD}`}
                    element={<ForgotPassword />}
                  />
                  <Route
                    path={`/${PublicRoutes.RESETPASSWORD}`}
                    element={<ResetPassword />}
                  />
                  <Route element={<AuthGuard />}>
                    <Route
                      path={`${PrivateRoutes.PRIVATE}/*`}
                      element={<Private />}
                    />
                  </Route>
                </RoutesWithNotFound>
              </NavigationScroll>
            </ThemeProvider>
          </BrowserRouter>
        </ColorModeContext.Provider>
      </LocalizationProvider>
    </Suspense>
  );
}

function App() {
  return (
    <Provider store={DataBuddiesStore}>
      <AppContent />
    </Provider>
  );
}

export default App;
